import { DevicePresenceStateDto } from '@/api/generator';
import { injectStrict } from '@/utils/injectStrict';
import { waitForConnectionState } from '@/utils/signalR';
import { HubConnectionBuilder, HubConnectionState, ISubscription } from '@microsoft/signalr';
import {
  computed,
  MaybeRefOrGetter,
  onMounted,
  onUnmounted,
  Ref,
  ref,
  toRef,
  watchEffect,
} from 'vue';
import { useMemoize } from '@vueuse/core';
import { DeviceStatusHubKey } from './severity';

export function getDeviceStatusHubConnectionSignalR() {
  const connection = new HubConnectionBuilder()
    .withUrl('/deviceStatusHub')
    .withAutomaticReconnect([0, 2000, 10000, 30000])
    .build();
  return connection;
}

const cachedStateValue = useMemoize((_deviceId: string | undefined) => {
  return ref<DevicePresenceStateDto | undefined>();
});

export function useDeviceStatusSignalR(
  deviceId: MaybeRefOrGetter<string | undefined>,
): Ref<DevicePresenceStateDto | undefined> {
  const connection = injectStrict(DeviceStatusHubKey);

  let subscription: ISubscription<DevicePresenceStateDto> | undefined;

  const id = toRef(deviceId);

  watchEffect(() => {
    if (id.value) {
      subscribe(id.value);
    }
  });

  async function subscribe(subscriptionId: string) {
    if (subscription) {
      subscription.dispose();
    }

    await waitForConnectionState(connection, HubConnectionState.Connected);

    subscription = connection
      .stream<DevicePresenceStateDto>('RegisterToDeviceStatusStreamAsync', subscriptionId)
      .subscribe({
        next: (value) => {
          if (value.id) {
            cachedStateValue(value.id).value = value;
          }
        },
        error: (value) => {
          throw new Error('Not able to update device  state', value);
        },
        complete: () => {
          throw new Error('Stream was completed unexpectedly');
        },
      });
  }

  onMounted(async () => {
    if (connection.state === HubConnectionState.Disconnected) {
      await connection.start();
    }
  });

  onUnmounted(async () => {
    subscription = undefined;
  });

  return computed(() => {
    return cachedStateValue(id.value).value;
  });
}
